import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {loadClubs} from '../../account/action/accountActions';
import AccountRoles from '../../account/enums/AccountRoles';
import PaymentModels from '../../account/enums/PaymentModels';
import ConfirmationDialog from '../../common/component/form/ConfirmationDialog';
import Table from '../../common/component/table/Table';
import DateRangesMs from '../../common/enums/DateRangesMs';
import {formatDate} from '../../common/util/DateUtil';

import {sortCaseInsensitive} from '../../common/util/SortUtil';
import FilterPopup from '../../training/component/FilterPopup';
import {DateFilterTimespan} from '../../training/enums/DateFilterTimespan';
import {noEntryOptional} from '../../training/util/TrainingUtilities';
import clubApi from '../api/clubApi';
import '../style/ClubOverview.less';
import ClubDialog from './ClubDialog';

function ClubOverview() {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const currentLanguage = useSelector(state => state.i18n.currentLanguage);
    const role = useSelector(state => state.account.role);
    const allClubs = useSelector(state => state.account.clubs);
    const [showClubDialog, setShowClubDialog] = useState(false);
    const [clubIdForDeletion, setClubIdForDeletion] = useState(undefined);
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(false);
    const [selectedClub, setSelectedClub] = useState(null);
    const [visibleClubs, setVisibleClubs] = useState([]);
    const [dateRangeFilter, setDateRangeFilter] = useState();
    const [paymentModelFilter, setPaymentModelFilter] = useState(Object.values(PaymentModels).map(model => {
        return {
            value: model,
            show: true,
            label: t(`Club.PAYMENT_MODEL_${model}`)
        };
    }));

    const history = useHistory();

    const reloadClubs = () => {
        dispatch(loadClubs());
    };

    useEffect(() => {
        filterVisibleClubs();
    }, [allClubs, paymentModelFilter, dateRangeFilter]);

    const paymentModelOptions = Object.values(PaymentModels).map(model => {
        return {
            value: model,
            label: t(`Club.PAYMENT_MODEL_${model}`)
        };
    });

    const dateRanges = [
        {
            label: t('Timespan.NO_DATE'),
            value: DateFilterTimespan.NO_DATE
        },
        {
            label: t('Timespan.NEXT_MONTH'),
            value: DateFilterTimespan.NEXT_MONTH
        },
        {
            label: t('Timespan.NEXT_ONE_TO_THREE_MONTHS'),
            value: DateFilterTimespan.NEXT_ONE_TO_THREE_MONTHS
        },
        {
            label: t('Timespan.NEXT_THREE_TO_SIX_MONTHS'),
            value: DateFilterTimespan.NEXT_THREE_TO_SIX_MONTHS
        },
        {
            label: t('Timespan.NEXT_SIX_TO_TWELVE_MONTHS'),
            value: DateFilterTimespan.NEXT_SIX_TO_TWELVE_MONTHS
        },
        {
            label: t('Timespan.OVER_ONE_YEAR_IN_FUTURE'),
            value: DateFilterTimespan.OVER_ONE_YEAR_IN_FUTURE
        }
    ];

    const getFilterDateRange = () => {
        const oneMonthAhead = Date.now() + DateRangesMs.MONTH;
        const threeMonthAhead = Date.now() + (DateRangesMs.MONTH * 3);
        const sixMonthAhead = Date.now() + (DateRangesMs.MONTH * 6);
        const oneYearAhead = Date.now() + DateRangesMs.YEAR;

        let minDate = Date.now();
        let maxDate = new Date(8640000000000000);

        if (dateRangeFilter === DateFilterTimespan.NEXT_MONTH) {
            maxDate = oneMonthAhead;
        } else if (dateRangeFilter === DateFilterTimespan.NEXT_ONE_TO_THREE_MONTHS) {
            minDate = oneMonthAhead;
            maxDate = threeMonthAhead;
        } else if (dateRangeFilter === DateFilterTimespan.NEXT_THREE_TO_SIX_MONTHS) {
            minDate = threeMonthAhead;
            maxDate = sixMonthAhead;
        } else if (dateRangeFilter === DateFilterTimespan.NEXT_SIX_TO_TWELVE_MONTHS) {
            minDate = sixMonthAhead;
            maxDate = oneYearAhead;
        } else if (dateRangeFilter === DateFilterTimespan.OVER_ONE_YEAR_IN_FUTURE) {
            minDate = oneYearAhead;
        } else if (dateRangeFilter === DateFilterTimespan.NO_DATE) {
            return {
                noDate: true
            };
        } else {
            return null;
        }

        return {
            minDate,
            maxDate
        };
    };

    const buildDateRangeValues = () => {
        return dateRanges.map(dateRange => {
            const show = dateRangeFilter === dateRange.value;
            return {
                ...dateRange,
                show: show
            };
        });
    };

    const handleDateFilterChange = (value) => {
        let dateRange;
        if (value && value.value && value.id < dateRanges.length) {
            dateRange = dateRanges[value.id].value;
        } else {
            dateRange = null;
        }
        setDateRangeFilter(dateRange);
    };

    function isPaidModelEndDateInFilterRange(filterDateRange, paidModelEndDateString) {
        if (filterDateRange && filterDateRange.noDate) {
            return !paidModelEndDateString;
        }

        const paidModelEndDate = paidModelEndDateString ? new Date(paidModelEndDateString) : null;
        return !filterDateRange || (paidModelEndDate && paidModelEndDate.getTime() >= filterDateRange.minDate && paidModelEndDate.getTime() <= filterDateRange.maxDate);
    }

    const filterVisibleClubs = () => {
        const paymentModelSet = new Set(paymentModelFilter?.filter(entry => entry.show).map(entry => entry.value));
        const filterDateRange = getFilterDateRange();

        const clubs = allClubs.filter((club) => {
            return paymentModelSet.has(club.paymentModel) && isPaidModelEndDateInFilterRange(filterDateRange, club.paidModelEndDate);
        });
        setVisibleClubs(clubs);
    };

    const handleChangePaymentModelFilter = (value) => {
        const newSelectedPaymentModels = [...paymentModelFilter];

        if (value === 'all') {
            newSelectedPaymentModels.forEach(disc => disc.show = true);
        } else if (value === 'none') {
            newSelectedPaymentModels.forEach(disc => disc.show = false);
        } else {
            if (value.id < newSelectedPaymentModels.length) {
                newSelectedPaymentModels[value.id].show = !newSelectedPaymentModels[value.id].show;
            }
        }
        setPaymentModelFilter(newSelectedPaymentModels);
    };

    const handleSave = () => {
        reloadClubs();
        setShowClubDialog(false);
        setSelectedClub(null);
    };

    const createClub = () => {
        setShowClubDialog(true);
    };

    const selectClub = (uuid) => {
        history.push(`club/${uuid}`);
    };

    const columns =
        [{
            Header: t('Club.SERIAL_NUMBER'),
            accessor: 'serialNumber',
            Cell: props => {
                const text = `${props.cell.row.original.serialNumber}`;
                return noEntryOptional(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.serialNumber, b.original.serialNumber)
        },
            {
                Header: t('Club.NAME'),
                accessor: row => noEntryOptional(row.name),
                Cell: props => {
                    const text = `${props.cell.row.original.name}`;
                    return noEntryOptional(text);
                },
                sortType: (a, b) => sortCaseInsensitive(a.original.name, b.original.name)
            }, {
            Header: t('Club.PAYMENT_MODEL'),
            accessor: 'paymentModel',
            width: '20%',
            disableFilters: true,
            Cell: props => {
                const text = t(`Club.PAYMENT_MODEL_${props.cell.row.original.paymentModel}`);
                return noEntryOptional(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.paymentModel, b.original.paymentModel)
        }, {
            Header: t('Club.END_OF_LICENSE'),
            accessor: 'paidModelEndDate',
            width: '20%',
            Cell: props => {
                if (props.cell.row.original.paidModelEndDate) {
                    return formatDate(currentLanguage, props.cell.row.original.paidModelEndDate);
                }
                return '';
            },
            disableFilters: true,
            sortType: (a, b) => sortCaseInsensitive(a.original.paidModelEndDate, b.original.paidModelEndDate)
        },
            {
                Header: '',
                accessor: 'id',
                width: '15%',
                disableFilters: true,
                Cell: props => {
                    return (
                        <>
                            {
                                role === AccountRoles.ADMINISTRATOR &&
                                <button
                                    className="btn btn-transparent-secondary btn-icon"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setClubIdForDeletion(props.cell.value);
                                        setShowDeletionConfirmation(true);
                                    }}
                                    title={t('Button.DELETE')}
                                >
                                    {<FontAwesomeIcon icon="trash"/>}
                                </button>
                            }
                            {
                                (role === AccountRoles.ADMINISTRATOR ||
                                    role === AccountRoles.CLUB_MANAGER) &&
                                <button
                                    className="btn btn-transparent-secondary btn-icon"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedClub(allClubs.find(club => club.id === props.cell.value));
                                        setShowClubDialog(true);
                                    }}
                                    title={t('Button.EDIT')}
                                >
                                    <FontAwesomeIcon icon="pencil-alt"/>
                                </button>
                            }
                        </>
                    );
                }
            }
        ];

    function deleteClub() {
        clubApi.deleteClub(clubIdForDeletion).then((response) => {
            if (response.data.success) {
                reloadClubs();
                setClubIdForDeletion(null);
            }
        });
    }

    const filterPopups = [null, null,
                          <FilterPopup options={paymentModelOptions}
                                       values={[...paymentModelFilter]}
                                       onChange={(value) => handleChangePaymentModelFilter(value)}
                                       selectAllOptions={() => handleChangePaymentModelFilter('all')}
                                       deselectAllOptions={() => handleChangePaymentModelFilter('none')}
                          />,
                          <FilterPopup options={dateRanges}
                                       values={buildDateRangeValues()}
                                       onChange={(value) => handleDateFilterChange(value)}
                                       isMulti={false}
                                       infoText={t('Club.END_OF_LICENSE_INFO')}
                          />
    ];

    const isFilteredArray = [false, false, !!paymentModelFilter?.find(c => !c.show), !!dateRangeFilter];

    return (
        <div className="club-overview">

            <div className="align-right">
                <h1>
                    {t('Club.CLUB_OVERVIEW')}
                </h1>
                {
                    role === AccountRoles.ADMINISTRATOR &&
                    <button
                        className="btn btn-primary ml-3"
                        onClick={createClub}
                    >
                        <FontAwesomeIcon icon="plus" className="mr-2"/>
                        {t('Button.ADD')}
                    </button>
                }
            </div>

            <div className="">
                <Table
                    data={visibleClubs}
                    columns={columns}
                    rowClick={selectClub}
                    sortable={true}
                    filterable={true}
                    popup={filterPopups}
                    isFiltered={isFilteredArray}
                    defaultFilterClass="club-table-filter"
                    initialSort={[{
                        id: 'name',
                        desc: false
                    }]}
                />
            </div>
            <ConfirmationDialog
                title={t('Dialog.DELETE_CLUB')}
                open={showDeletionConfirmation}
                description={t('Dialog.DELETE_CLUB_QUESTION')}
                accept={() => {
                    deleteClub();
                    setClubIdForDeletion(null);
                    setShowDeletionConfirmation(false);
                }}
                cancel={() => {
                    setClubIdForDeletion(null);
                    setShowDeletionConfirmation(false);
                }}
                acceptLabel={t('Button.DELETE')}
            />
            <ClubDialog
                open={showClubDialog}
                onCancel={() => {
                    setShowClubDialog(false);
                    setSelectedClub(null);
                }}
                onSave={handleSave}
                club={selectedClub}
            />
        </div>
    );
}

export default ClubOverview;
